import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { 
  Users,
  Search,
  Filter,
  Star,
  Calendar,
  Clock,
  Zap,
  Users as UsersIcon,
  Briefcase,
  Clock as ClockIcon,
  Settings,
  Lock,
  Plus,
} from 'lucide-react';
import InterviewForm from '@/components/interviews/interview-form';
import RecruitingProjectForm from '@/components/dashboard/recruiting-project-form';

export function HireDashboard() {
  const { user, currentMode, hasLicense } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState<any[]>([]);
  const [metrics, setMetrics] = useState({
    totalCandidates: 0,
    newApplications: 0,
    scheduledInterviews: 0,
    offersSent: 0,
    applicationsByStatus: {
      new: 0,
      reviewed: 0,
      interviewed: 0,
      offered: 0,
      rejected: 0,
    },
    applicationTrends: {
      thisWeek: 0,
      lastWeek: 0,
      percentageChange: 0,
    },
    departmentDistribution: {
      total: 0,
      distribution: {
        tech: 0,
        sales: 0,
        marketing: 0,
        other: 0
      }
    }
  });
  const [interviewFormOpen, setInterviewFormOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [projectFormOpen, setProjectFormOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch applications with related data
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('applications')
        .select(`
          id,
          status,
          application_date,
          job_id,
          profiles (
            id,
            full_name,
            title,
            location
          ),
          recruiting_projects (
            id,
            title,
            department
          )
        `)
        .eq('recruiting_projects.profile_id', user?.id)
        .order('application_date', { ascending: false });

      if (applicationsError) throw applicationsError;

      // Fetch scheduled interviews count
      const { data: interviewsData, error: interviewsError } = await supabase
        .from('interviews')
        .select('id')
        .eq('profile_id', user?.id)
        .in('status', ['scheduled', 'confirmed'])
        .gte('date', new Date().toISOString().split('T')[0]);

      if (interviewsError) throw interviewsError;

      // Calculate metrics from applications data
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      
      const thisWeekApplications = applicationsData?.filter(app => 
        new Date(app.application_date) >= oneWeekAgo
      ).length || 0;
      
      const lastWeekApplications = applicationsData?.filter(app => 
        new Date(app.application_date) >= twoWeeksAgo && 
        new Date(app.application_date) < oneWeekAgo
      ).length || 0;

      const percentageChange = lastWeekApplications === 0 
        ? 100 
        : ((thisWeekApplications - lastWeekApplications) / lastWeekApplications) * 100;

      // Group applications by status
      const statusCounts = applicationsData?.reduce((acc: any, app) => {
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, {}) || {};

      // Group applications by department
      const departmentCounts = applicationsData?.reduce((acc: any, app) => {
        const dept = app.recruiting_projects?.department?.toLowerCase() || 'other';
        const category = dept.includes('tech') ? 'tech' 
          : dept.includes('sales') ? 'sales'
          : dept.includes('marketing') ? 'marketing'
          : 'other';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {
        tech: 0,
        sales: 0,
        marketing: 0,
        other: 0
      });

      setApplications(applicationsData || []);
      setMetrics({
        totalCandidates: applicationsData?.length || 0,
        newApplications: statusCounts.new || 0,
        scheduledInterviews: interviewsData?.length || 0,
        offersSent: statusCounts.offered || 0,
        applicationsByStatus: {
          new: statusCounts.new || 0,
          reviewed: statusCounts.reviewed || 0,
          interviewed: statusCounts.interviewed || 0,
          offered: statusCounts.offered || 0,
          rejected: statusCounts.rejected || 0,
        },
        applicationTrends: {
          thisWeek: thisWeekApplications,
          lastWeek: lastWeekApplications,
          percentageChange,
        },
        departmentDistribution: {
          total: applicationsData?.length || 0,
          distribution: departmentCounts
        }
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load dashboard data',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // If not in hire mode or no license, show upgrade card
  if (currentMode !== 'hire' || !hasLicense('hire')) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Recruiter License Required</CardTitle>
            </div>
            <CardDescription>
              You need an active recruiter license to access hiring features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/pricing">Upgrade Now</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Recruiting Dashboard</h1>
          <p className="text-muted-foreground">
            Track your recruiting projects and candidate pipeline
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setProjectFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/search')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Candidates</p>
                <h3 className="text-2xl font-bold mt-2">{metrics.totalCandidates}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <UsersIcon className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/dashboard/applications')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">New Applications</p>
                <h3 className="text-2xl font-bold mt-2">{metrics.newApplications}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/dashboard/interviews')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Scheduled Interviews</p>
                <h3 className="text-2xl font-bold mt-2">{metrics.scheduledInterviews}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => navigate('/dashboard/offers')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Offers Sent</p>
                <h3 className="text-2xl font-bold mt-2">{metrics.offersSent}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
                <Star className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Application Pipeline */}
        <Card>
          <CardHeader>
            <CardTitle>Application Pipeline</CardTitle>
            <CardDescription>Current candidate pipeline status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(metrics.applicationsByStatus).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm font-medium capitalize">{status}</span>
                  <div className="flex items-center">
                    <span className="font-bold">{count}</span>
                    <div className="w-24 h-2 ml-4 rounded-full bg-primary/20">
                      <div 
                        className="h-full rounded-full bg-primary" 
                        style={{ 
                          width: `${(count / metrics.totalCandidates) * 100}%` 
                        }} 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Department Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Department Distribution</CardTitle>
            <CardDescription>Candidates by department</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(metrics.departmentDistribution.distribution).map(([dept, count]) => (
                <div key={dept} className="flex items-center justify-between">
                  <span className="text-sm font-medium capitalize">{dept}</span>
                  <div className="flex items-center">
                    <span className="font-bold">{count}</span>
                    <div className="w-24 h-2 ml-4 rounded-full bg-primary/20">
                      <div 
                        className="h-full rounded-full bg-primary" 
                        style={{ 
                          width: `${(count / metrics.departmentDistribution.total) * 100}%` 
                        }} 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Interview Form */}
      {selectedApplication && (
        <InterviewForm
          open={interviewFormOpen}
          onOpenChange={setInterviewFormOpen}
          onSuccess={() => {
            setInterviewFormOpen(false);
            fetchDashboardData();
          }}
        />
      )}

      {/* Project Form */}
      <RecruitingProjectForm
        open={projectFormOpen}
        onOpenChange={setProjectFormOpen}
        onSuccess={() => {
          setProjectFormOpen(false);
          fetchDashboardData();
        }}
      />
    </div>
  );
}